import { getFullMonthName } from '@/helpers';
import { Milestone, SummerCampaign } from '@/models/leaderboard';
import { Routes } from '@/router';
import { SummerExtrasErrorEvent, SummerExtrasStartOrderClickEvent } from '@/services/analytics/events';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
@Component({
  name: 'CmgLeaderboardProgress',
})
export default class CmgLeaderboardProgress extends Vue {

  get isInErrorState(): boolean {
    return this.$appStore.state.leaderboard.isInErrorState;
  }

  get currentMonthIndex(): number {
    // To assist in testing - at least a month index of 5 - June since campaigns start in June, but development occurring in May
    return Math.max(5, new Date().getMonth());
  }

  get summerCampaign(): SummerCampaign | undefined {
    return this.$appStore.state.leaderboard.summerCampaigns.find(x => x.month === this.currentMonthIndex);
  }

  get title(): string {
    return this.summerCampaign?.title || `${getFullMonthName(new Date())} Challenge`;
  }

  get isChallengeComplete(): boolean {
    if (this.hasData) {
      return this.summerCampaign!.milestones.filter(x => !x.completed).length === 0;
    }

    return false;
  }

  get isLastChallenge(): boolean {
    // campaigns are ordered from earliest to latest in the leaderboard.service
    const lastCampaignMonth = this.$appStore.state.leaderboard.summerCampaigns.slice(-1)[0]?.month;
    return this.currentMonthIndex === lastCampaignMonth;
  }

  get daysLeft(): number {
    const daysInMonth = new Date(new Date().getFullYear(), this.currentMonthIndex - 1, 0).getDate();
    // Adding 1 so that the day calculation is inclusive, e.g. June 1 says 30 days left (30 - 1) + 1
    return (daysInMonth - (new Date().getDate())) + 1;
  }

  get milestones(): Milestone[] {
    const monthlyCampaign = this.summerCampaign;
    if (monthlyCampaign) {
      return monthlyCampaign.milestones;
    }
    return [];
  }

  get totalMilestonesCount(): number {
    return (this.hasData) ? this.summerCampaign!.totalNumberOfSteps : 7;
  }

  get completedMilestonesCount(): number {
    return this.hasData ? this.summerCampaign!.currentStep : 0;
  }

  get hasData(): boolean {
    return this.summerCampaign !== undefined;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get statBox1Img(): string | undefined {
    switch (this.currentMonthIndex) {
      case 5:
        return !this.isMobile ? this.$appStore.state.cms.images.loyalty.leaderboardPinkYellowBoxShort1.desktop : this.$appStore.state.cms.images.loyalty.leaderboardPinkYellowBoxShort1.mobile;
      case 6:
        return !this.isMobile ? this.$appStore.state.cms.images.loyalty.leaderboardGreenYellowBoxShort1.desktop : this.$appStore.state.cms.images.loyalty.leaderboardGreenYellowBoxShort1.mobile;
      case 7:
        return !this.isMobile ? this.$appStore.state.cms.images.loyalty.leaderboardBlueGreenBoxShort1.desktop : this.$appStore.state.cms.images.loyalty.leaderboardBlueGreenBoxShort1.mobile;
      default:
        return !this.isMobile ? this.$appStore.state.cms.images.loyalty.leaderboardPinkYellowBoxShort1.desktop : this.$appStore.state.cms.images.loyalty.leaderboardPinkYellowBoxShort1.mobile;
    }
  }

  get statBox2Img(): string | undefined {
    switch (this.currentMonthIndex) {
      case 5:
        return !this.isMobile ? this.$appStore.state.cms.images.loyalty.leaderboardPinkYellowBoxShort2.desktop : this.$appStore.state.cms.images.loyalty.leaderboardPinkYellowBoxShort2.mobile;
      case 6:
        return !this.isMobile ? this.$appStore.state.cms.images.loyalty.leaderboardGreenYellowBoxShort2.desktop : this.$appStore.state.cms.images.loyalty.leaderboardGreenYellowBoxShort2.mobile;
      case 7:
        return !this.isMobile ? this.$appStore.state.cms.images.loyalty.leaderboardBlueGreenBoxShort2.desktop : this.$appStore.state.cms.images.loyalty.leaderboardBlueGreenBoxShort2.mobile;
      default:
        return !this.isMobile ? this.$appStore.state.cms.images.loyalty.leaderboardPinkYellowBoxShort2.desktop : this.$appStore.state.cms.images.loyalty.leaderboardPinkYellowBoxShort2.mobile;
    }
  }

  setLoadingStatus(loading: boolean) {
    this.$services.ui.setAppBlur(loading);
    this.$services.ui.setAppPepperVisibility(loading);
  }

  async onClickCta() {
    if (this.isInErrorState) {
      this.$services.analytics.send(new SummerExtrasErrorEvent());
      this.setLoadingStatus(true);
      await this.$services.leaderboard.getLeaderboardData()
        .finally(() => this.setLoadingStatus(false));
      return;
    }

    if (this.isChallengeComplete) {
      this.$router.push({ name: Routes.RewardsExtras });
    } else {
      this.$router.push({ name: Routes.Order, hash: '#menu' });
      this.$services.analytics.send(new SummerExtrasStartOrderClickEvent(this.title));
    }
  }
}
